import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { on, off, isElementVisible } from 'utils/dom/dom';
import classnames from 'classnames';

const VIEWPORT_EVENTS = [
	'resize',
	'scroll',
	'touchstart',
	'touchmove',
	'touchend',
	'load',
];
export const DEFAULT_DELAY = 1000;

export function ViewportAction({
	children,
	className,
	delay = DEFAULT_DELAY,
	percentVisible = 100,
	onVisible,
}) {
	const elRef = useRef(null);
	const triggeredRef = useRef(false);
	const triggerTimeoutRef = useRef(null);
	const [isVisible, setIsVisible] = useState(false);

	const getRef = useCallback((element) => {
		elRef.current = element;
	}, []);

	const handleViewportChange = useCallback(() => {
		if (triggeredRef.current) return;
		setIsVisible(isElementVisible(elRef.current, percentVisible));
	}, [percentVisible]);

	useEffect(() => {
		on(VIEWPORT_EVENTS, handleViewportChange);
		handleViewportChange();
		return () => {
			off(VIEWPORT_EVENTS, handleViewportChange);
			clearTimeout(triggerTimeoutRef.current);
		};
	}, [handleViewportChange]);

	useEffect(() => {
		if (triggeredRef.current) return;

		if (isVisible) {
			triggerTimeoutRef.current = setTimeout(() => {
				triggeredRef.current = true;
				onVisible(delay);
			}, delay);
		}

		return () => {
			clearTimeout(triggerTimeoutRef.current);
		};
	}, [isVisible, delay, onVisible]);

	return (
		<div
			ref={getRef}
			className={classnames('viewport-action', className)}
		>
			{children}
		</div>
	);
}

ViewportAction.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	delay: PropTypes.number,
	percentVisible: PropTypes.number,
	onVisible: PropTypes.func,
};

export default ViewportAction;
